import React, { useState, useMemo, useEffect, useRef } from 'react'
import { MY_PGA_COM_HOST } from 'env'
import { useSnackbar } from 'notistack'
import {
  Button,
  CircularProgress,
  Container,
  DialogTitle,
  DialogActions,
  DialogContent,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core'
import useStyles from './styles'
import { getTimePeriodWithTimezone } from 'utils/dateUtils'
import Dialog from 'components/dialog'
import SendOutlinedIcon from 'components/material-symbol/icons/Send'
import { centsToDollars } from 'lib/utils/number'
import propTypes from 'prop-types'
import { gtmEvent } from 'lib/gtm'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from 'components/material-symbol/icons/Close'

import { Elements } from '@stripe/react-stripe-js'
import PaymentForm from 'pages/payment-request/components/payment-form'
import { stripePromise } from 'lib/stripe'

import { useSendPaymentRequest } from 'lib/graphql/mutations/payment-request-mutation.js'
import { usePrepareRecordBookingPayment } from 'lib/graphql/mutations/prepare-record-booking-payment-mutation.js'
import PaymentLink from './payment-link'
import QRCodeComponent from './qr-code-component'

const DialogCloseButton = ({ styles, closeDialog }) => (
  <IconButton
    aria-label="close"
    className={styles.closeButton}
    onClick={closeDialog}
  >
    <CloseIcon />
  </IconButton>
)

const RequestPaymentDialog = ({ trigger, enrollment, onPaymentSuccess }) => {
  const paymentFormRef = useRef()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sendPaymentRequest, { loading }] = useSendPaymentRequest()
  const [
    prepareRecordBookingPayment,
    { data, loading: pendingPaymentLoading },
  ] = usePrepareRecordBookingPayment()
  const { enqueueSnackbar } = useSnackbar()
  const [selectedTab, setSelectedTab] = useState('link')
  const [clientSecret, setClientSecret] = useState('')
  useEffect(() => {
    if (enrollment) {
      prepareRecordBookingPayment({
        variables: {
          enrollmentId: enrollment.id,
        },
      })
    }
  }, [prepareRecordBookingPayment, enrollment])
  useEffect(() => {
    if (data?.prepareRecordBookingPayment?.success) {
      setClientSecret(
        data?.prepareRecordBookingPayment?.paymentIntent?.clientSecret,
      )
    }
  }, [data])

  const {
    category,
    id: enrollmentId,
    startDateTime,
    endDateTime,
    priceInCents = 0,
    student,
    timezone,
    title,
  } = enrollment
  const classes = useStyles()
  const timePeriod = getTimePeriodWithTimezone(
    startDateTime,
    endDateTime,
    timezone,
  )
  const price = centsToDollars(priceInCents)
  const paymentOptions = useMemo(() => {
    const appearance = {
      theme: 'stripe',
    }
    const options = {
      clientSecret,
      appearance,
    }

    return [
      {
        label: 'Payment Link',
        value: 'link',
        description:
          'Send a payment request to your student for this session. Once paid, both of you will receive a notification.',
        component: (
          <PaymentLink
            timePeriod={timePeriod}
            title={title}
            student={student}
            price={price}
            classes={classes}
            category={category}
          />
        ),
      },
      {
        label: 'QR Code',
        value: 'qrCode',
        description:
          "Have your student scan this QR code and enter their payment information. It's a quick and easy way to collect payments on the spot!",
        component: (
          <QRCodeComponent
            classes={classes}
            enrollmentUUID={enrollment.paymentRequestToken}
          />
        ),
      },
      {
        label: 'Credit Card',
        value: 'creditCard',
        description: 'Manually enter credit card details to collect payment.',
        component: (
          <Elements options={options} stripe={stripePromise}>
            <PaymentForm
              ref={paymentFormRef}
              enrollment={enrollment}
              clientSecret={clientSecret}
              redirectUrl={`${MY_PGA_COM_HOST}/payment/${enrollment.paymentRequestToken}/success`}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
            />
          </Elements>
        ),
      },
    ]
  }, [
    timePeriod,
    title,
    student,
    price,
    classes,
    category,
    enrollment,
    clientSecret,
    isSubmitting,
  ])

  const paymentTabs = () => {
    return (
      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        style={{ borderBottom: '1px solid #e0e0e0' }}
      >
        {paymentOptions.map((option, index) => (
          <Tab
            label={option.label}
            value={option.value}
            key={index}
            data-testid={`payment-request-tab-${option.value}`}
          />
        ))}
      </Tabs>
    )
  }

  const renderPaymentSwitch = (tab = 'link') => {
    const option = paymentOptions.find(option => option.value === tab)
    return (
      <>
        <Typography
          variant="body1"
          gutterBottom
          className={classes.componentSpacing}
        >
          {option.description}
        </Typography>
        {option.component}
      </>
    )
  }

  if (loading || pendingPaymentLoading) {
    return <CircularProgress color="primary" />
  }
  return (
    <Dialog trigger={trigger} maxWidth="sm" fullWidth>
      {({ closeDialog }) => {
        const onPaymentRequestConfirm = () => {
          sendPaymentRequest({ variables: { enrollmentId } }).then(
            ({ data }) => {
              if (data.sendPaymentRequest.success) {
                enqueueSnackbar('Payment request successfully sent', {
                  variant: 'success',
                })
                gtmEvent({
                  event: 'request-payment',
                  requestAction: 'request',
                  reqestEnrollment: enrollmentId,
                })
                closeDialog()
                onPaymentSuccess()
              } else {
                enqueueSnackbar(data.sendPaymentRequest.message, {
                  variant: 'error',
                })
              }
            },
          )
        }
        const handleSubmitStripe = async () => {
          await paymentFormRef.current.submitForm()
          gtmEvent({
            event: 'request-payment',
            requestAction: 'payment',
            reqestEnrollment: enrollmentId,
          })
          if (!isSubmitting) {
            closeDialog()
            // this needs to wait like .5 seconds to allow the Stripe webhook to fire
            setTimeout(() => {
              onPaymentSuccess()
            }, 500)
          }
        }

        return (
          <Container className={classes.dialogContent}>
            <DialogTitle>
              <Typography variant="h5">Collect Payment</Typography>
              <DialogCloseButton styles={classes} closeDialog={closeDialog} />
            </DialogTitle>
            <DialogContent style={{ padding: 0 }}>
              {paymentTabs()}
              <Container>{renderPaymentSwitch(selectedTab)}</Container>
              {loading ? <CircularProgress color="primary" /> : null}
            </DialogContent>
            {selectedTab === 'link' && (
              <DialogActions className={classes.actions}>
                <Button color="primary" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<SendOutlinedIcon />}
                  onClick={onPaymentRequestConfirm}
                  data-testid="send-payment-request"
                >
                  Request
                </Button>
              </DialogActions>
            )}
            {selectedTab === 'creditCard' && clientSecret && (
              <DialogActions className={classes.actions}>
                <Button color="primary" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleSubmitStripe}
                  disabled={isSubmitting}
                >
                  Process Payment
                </Button>
              </DialogActions>
            )}
          </Container>
        )
      }}
    </Dialog>
  )
}

export default RequestPaymentDialog

RequestPaymentDialog.propTypes = {
  trigger: propTypes.any,
  enrollment: propTypes.object,
  onPaymentSuccess: propTypes.func,
}
